import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LuTriangleAlert, LuClock, LuCalendarDays } from 'react-icons/lu';
import { studentApi } from '../../services/api';
import TestRunner from '../../components/TestRunner';
import LessonIcon, { levelFromGrade } from '../../components/LessonIcon';
import { useAuth } from '../../contexts/AuthContext';
import AwaitingClassNotice from '../../components/AwaitingClassNotice';
import { AWAITING_ASSIGNMENTS_TEXT, studentClassStatus } from '../../utils/classStatus';
import SegmentedSwitch from '../../components/SegmentedSwitch';
import FilterPills from '../../components/FilterPills';

// react-icons' `IconType` return type is `ReactNode` in this version, which
// React 19's stricter JSX typing rejects when used as a component. Cast to a
// concrete component type so the icon elements render cleanly (same pattern
// as SmartBoard.tsx).
type IconCmp = React.FC<{ size?: number; className?: string; color?: string }>;
const AlertIcon = LuTriangleAlert as IconCmp;
const ClockLuIcon = LuClock as IconCmp;
const CalendarIcon = LuCalendarDays as IconCmp;

interface Assignment {
  /** The SUBMISSION id — what every per-student call takes. */
  id: string;
  /** The assignment it belongs to — what a Çalışma Planım entry points at. */
  assignmentId: string;
  title: string;
  subject: string;
  teacherName?: string | null;
  type: 'test' | 'odev';
  status: 'pending' | 'completed';
  openDate: string;
  closeDate: string;
  closeDateRaw?: string | null;
  // Raw due_date only (no created_at fallback) — null ⇒ süresiz. Used by the
  // Teslim Tarihi filter so süresiz homeworks aren't bucketed by creation date.
  dueDateRaw?: string | null;
  questionCount: number;
  correctCount: number;
  wrongCount: number;
  blankCount: number;
  totalPoints: number;
  earnedPoints: number;
  timeSpent: string;
  timeSpentSeconds: number | null;
  completedAt: string | null;
  progress: number;
  teacherNote?: string;
  contentItems?: { name: string; format: string }[];
  uploadedItems?: { name: string; format: string }[];
  bookId?: number;
  sectionLabel?: string;
  // P3: new-model test identity from the [BOOK:…|TEST:…] tag (book_link.test_id)
  testId?: number | null;
  attachmentUrl?: string | null;
  // Diğer Ödev — the student's own uploaded submission (signed) + their note.
  submissionFileUrl?: string | null;
  studentNotes?: string | null;
  // Teacher's grade + comment, once the Diğer Ödev has been graded.
  graded?: boolean;
  gradeScore?: number | null;
  teacherFeedback?: string | null;
  // "Geç Teslim": actually turned in after the deadline (computed by backend).
  late?: boolean;
  // Manual answer key — only present once the teacher releases it.
  answersReleased?: boolean;
  answerKey?: string | null;
  // HW-1: optional metadata on a Diğer Ödev. Backend parses [OTHER:...] tag
  // out of the description — any or all keys may be missing.
  otherFields?: {
    questionCount?: string;
    subject?: string;
    unit?: string;
    outcome?: string;
    answers?: string;
  } | null;
  // D6: öğretmenin ödeve eklediği kazanımlar (MEB müfredat + kitap).
  learningOutcomes: { type: 'meb' | 'book'; id: number; code: string | null; label: string }[];
}

type Modal =
  | null
  | { type: 'teste-basla'; assignment: Assignment }
  | { type: 'sonuclar'; assignment: Assignment }
  | { type: 'cevaplar'; assignment: Assignment }
  | { type: 'diger-odev'; assignment: Assignment }
  | { type: 'odev-yukle'; assignment: Assignment }
  | { type: 'yeniden-yukle-onay'; assignment: Assignment };

const CloseButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    onClick={onClick}
    className="w-[48px] h-[48px] rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors absolute top-6 right-6"
  >
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="4" x2="20" y2="20" />
      <line x1="20" y1="4" x2="4" y2="20" />
    </svg>
  </button>
);

const ClockIcon: React.FC<{ size?: number; color?: string }> = ({ size = 18, color = '#1e1e1e' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

// "Started but incomplete" detection. HomeworkTestRunner stores its draft in
// localStorage keyed by `homework-test:ass-{assignmentId}`, so we can probe
// it cheaply on render to paint the pending card yellow when the student has
// answered at least one question and not yet submitted.
function getAssignmentDraftProgress(assignmentId: string): { started: boolean; answeredCount: number } {
  try {
    const raw = localStorage.getItem(`homework-test:ass-${assignmentId}`);
    if (!raw) return { started: false, answeredCount: 0 };
    const saved = JSON.parse(raw);
    const answeredCount = saved?.answers && typeof saved.answers === 'object'
      ? Object.keys(saved.answers).length
      : 0;
    return { started: answeredCount > 0, answeredCount };
  } catch {
    return { started: false, answeredCount: 0 };
  }
}

// T25: son tarih — tarih + saat her zaman gösterilir (23:59 dahil). due_date bir
// UTC anıdır; TR duvar-saati sabit UTC+3 (backend school-time ile aynı varsayım)
// — yalnızca GÖSTERİM içindir; aciliyet hesabı mutlak ms farkı kullanır (getDeadline).
function fmtDeadline(iso: string | null | undefined): string {
  if (!iso) return 'Süresiz';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Süresiz';
  const tr = new Date(d.getTime() + 3 * 3600 * 1000);
  const dd = String(tr.getUTCDate()).padStart(2, '0');
  const mo = String(tr.getUTCMonth() + 1).padStart(2, '0');
  const yy = tr.getUTCFullYear();
  const hh = tr.getUTCHours();
  const mn = tr.getUTCMinutes();
  return `${dd}.${mo}.${yy} ${String(hh).padStart(2, '0')}:${String(mn).padStart(2, '0')}`;
}

function mapSubmission(s: any): Assignment {
  const ass = s.assignment || {};
  const grade = s.assignment_grades;
  const attempt = s.attempt_stats ?? null;
  const isCompleted = ['submitted', 'graded', 'returned'].includes(s.status);
  const bookLink = ass.book_link;
  const teacher = ass.users ?? ass.teacher ?? ass.created_by_user ?? null;
  const teacherName = teacher
    ? `${teacher.first_name ?? ''} ${teacher.last_name ?? ''}`.trim() || null
    : null;

  // Use attempt stats for accurate counts once solved; before any attempt
  // (attempt_stats null), fall back to the section's own question count from
  // the assignment's book_link so the "Ödeve Başla" modal shows the real
  // "Soru Sayısı" and doesn't wrongly block a never-attempted test homework.
  const questionCount = attempt?.score_total ?? bookLink?.question_count ?? 0;
  const correctCount  = attempt?.score_correct ?? 0;
  const wrongCount    = attempt?.score_wrong ?? 0;
  const blankCount    = attempt?.score_blank ?? 0;
  const earnedPoints  = grade?.score ?? (attempt ? Math.round((correctCount / (questionCount || 1)) * 100) : 0);

  const secs = s.timeSpentSeconds ?? attempt?.time_spent_seconds ?? null;
  let timeSpent = '-';
  if (secs != null && secs > 0) {
    const m = Math.floor(secs / 60);
    const sec = secs % 60;
    timeSpent = m > 0 ? `${m} Dk ${sec > 0 ? `${sec} Sn` : ''}`.trim() : `${sec} Sn`;
  }

  return {
    id: String(s.id),
    assignmentId: String(s.assignmentId ?? ass.id ?? ''),
    title: ass.title || 'Ödev',
    subject: ass.subject?.name || '',
    teacherName,
    type: ass.assignment_type === 'other' ? 'odev' : 'test',
    status: isCompleted ? 'completed' : 'pending',
    openDate: ass.created_at ? new Date(ass.createdAt || ass.created_at).toLocaleDateString('tr-TR') : 'Süresiz',
    closeDate: fmtDeadline(ass.due_date),
    closeDateRaw: ass.due_date ?? ass.created_at ?? null,
    dueDateRaw: ass.due_date ?? null,
    questionCount,
    correctCount,
    wrongCount,
    blankCount,
    totalPoints: 100,
    earnedPoints,
    timeSpent,
    timeSpentSeconds: secs,
    completedAt: s.submittedAt ?? attempt?.submitted_at ?? null,
    progress: isCompleted ? 100 : 0,
    teacherNote: ass.description,
    bookId: bookLink?.book_id,
    sectionLabel: bookLink?.section_label,
    testId: bookLink?.test_id ?? null,
    attachmentUrl: ass.attachment_url ?? null,
    submissionFileUrl: s.submission_file_url ?? null,
    studentNotes: s.student_notes ?? null,
    graded: s.status === 'graded',
    gradeScore: grade?.score != null ? Number(grade.score) : null,
    teacherFeedback: grade?.feedback ?? null,
    late: !!s.late,
    answersReleased: ass.answers_released ?? false,
    answerKey: ass.answer_key ?? null,
    otherFields: ass.other_fields ?? null,
    learningOutcomes: ass.learning_outcomes ?? [],
  };
}


// D6: ödeve eklenen MEB kazanımı / kitap konusu rozetleri. PLE subject_units
// are labelled as konu and are never presented as MEB kazanımı.
const OutcomeChips: React.FC<{ outcomes: Assignment['learningOutcomes'] }> = ({ outcomes }) => {
  if (!outcomes || outcomes.length === 0) return null;
  return (
    <div className="bg-white rounded-[12px] p-4 border border-gray-200 mb-4">
      <p className="text-[13px] font-bold text-[#3973bf] mb-2">MEB Kazanımları / Kitap Konuları</p>
      <div className="flex flex-wrap gap-2">
        {outcomes.map((o, i) => (
          <span key={i} className="rounded-full px-3 py-1"
            style={{
              background: o.type === 'meb' ? '#eef3fd' : '#fdeef5',
              color: o.type === 'meb' ? '#3973bf' : '#e5599b',
              fontFamily: 'Inter', fontSize: 12, fontWeight: 500,
            }}>
            {o.type === 'meb' ? 'MEB · ' : 'Konu · '}{o.code ? `${o.code} · ` : ''}{o.label}
          </span>
        ))}
      </div>
    </div>
  );
};

// T3: ödev son-tarih rozeti — HER kartta gösterilir (Kapanış metni kaldırıldı).
// Kalan süreye göre kademeli renk + ikon + metin, dolu kırmızı kart yerine
// kontrollü sol-kenar vurgusu. closeDateRaw zaten 23:59 TR anını (UTC+3) tuttuğu
// için ms farkı istemci saat diliminden bağımsız doğru. Tamamlanmış ödevde
// aciliyet anlamsızdır → nötr tarih; "Yapılmadı" yalnızca yapılmamış + süresi
// geçmiş ödevlerde çıkar.
type DeadlineTier = 'overdue' | 'urgent' | 'soon' | 'normal' | 'done' | 'none';
interface DeadlineInfo {
  tier: DeadlineTier;
  label: string;                       // rozetin tam metni
  icon: 'alert' | 'clock' | 'calendar';
  fg: string;
  bg: string;
  accent: string; // sol-kenar şerit rengi ('transparent' = vurgu yok)
}
const DAY_MS = 86400000;
const DL_GRAY = { fg: '#6b7280', bg: '#f3f4f6' };
const DL_RED = { fg: '#dc2626', bg: '#fee2e2' };
const DL_AMBER = { fg: '#b45309', bg: '#fef3c7' };
function getDeadline(closeDateRaw: string | null | undefined, closeDate: string, completed: boolean): DeadlineInfo {
  if (!closeDateRaw) return { tier: 'none', label: 'Süresiz', icon: 'calendar', ...DL_GRAY, accent: 'transparent' };
  const due = new Date(closeDateRaw).getTime();
  if (Number.isNaN(due)) return { tier: 'none', label: closeDate || 'Süresiz', icon: 'calendar', ...DL_GRAY, accent: 'transparent' };
  // Done: the deadline is history — show it neutrally, never an urgency/"Yapılmadı".
  if (completed) return { tier: 'done', label: closeDate, icon: 'calendar', ...DL_GRAY, accent: 'transparent' };
  const diff = due - Date.now();
  if (diff < 0) return { tier: 'overdue', label: `Yapılmadı · ${closeDate}`, icon: 'alert', ...DL_RED, accent: '#dc2626' };
  const hours = diff / 3600000;
  if (hours <= 24) {
    return {
      tier: 'urgent',
      label: `${hours < 1 ? 'Son dakikalar' : `${Math.max(1, Math.floor(hours))} saat kaldı`} · ${closeDate}`,
      icon: 'alert', ...DL_RED, accent: '#dc2626',
    };
  }
  const days = Math.ceil(diff / DAY_MS);
  if (days <= 3) return { tier: 'soon', label: `${days} gün kaldı · ${closeDate}`, icon: 'clock', ...DL_AMBER, accent: '#f59e0b' };
  return { tier: 'normal', label: closeDate, icon: 'calendar', ...DL_GRAY, accent: 'transparent' };
}

// Bir ödevin teslim tarihi geçmiş mi? Geç kalınan testler çözülemez (aşağıda
// "Süreyi Başlat" butonu bu kontrolle kilitlenir).
function isPastDue(closeDateRaw: string | null | undefined): boolean {
  if (!closeDateRaw) return false;
  const t = new Date(closeDateRaw).getTime();
  return !Number.isNaN(t) && t < Date.now();
}

const DeadlineBadge: React.FC<{ info: DeadlineInfo }> = ({ info }) => {
  const Icon = info.icon === 'alert' ? AlertIcon : info.icon === 'clock' ? ClockLuIcon : CalendarIcon;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 self-start" style={{ background: info.bg, color: info.fg }}>
      <Icon size={13} color={info.fg} />
      <span style={{ fontFamily: 'Inter', fontSize: 12.5, fontWeight: 700 }}>{info.label}</span>
    </span>
  );
};

const StudentAssignments: React.FC = () => {
  // Çalışma Planım deep-link: navigate('/student/odevler', { state: { subjectFilter, openAssignmentId } }).
  // We narrow the list to the slot's subject when subjectFilter is set; if
  // the navigator also passed an exact assignment id (rare — requires backend
  // to FK schedule entries to assignments), auto-open that one's modal.
  const { state } = useLocation() as { state?: { subjectFilter?: string; openAssignmentId?: string } };
  const navigate = useNavigate();
  const { user } = useAuth();
  const studentLevel = levelFromGrade((user as any)?.grade?.grade_number ?? null);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<Modal>(null);
  // When set, the HomeworkTestRunner takes over the screen for this homework.
  const [runningTest, setRunningTest] = useState<Assignment | null>(null);
  const [subjectFilter, setSubjectFilter] = useState<string>(state?.subjectFilter ?? '');
  // True only when the current subject filter arrived via the Çalışma Planı
  // deep-link (Schedule.tsx navigate state) — drives the "Çalışma Planından
  // geldiniz" banner. Cleared the moment the student picks a subject chip
  // themselves, so manual filtering never shows that banner.
  const [fromSchedule, setFromSchedule] = useState<boolean>(!!state?.subjectFilter);
  // Two tabs: "Ödevler" = not-done (open, still to do); "Notlandırma" = work the
  // student has acted on (submitted/awaiting) + graded/released results.
  const [tab, setTab] = useState<'odevler' | 'notlandirma'>('odevler');
  const [timeFilter, setTimeFilter] = useState<'all' | 'today' | 'week' | 'month' | 'undated'>('all');
  // Transient confirmation banner (e.g. after a Diğer Ödev submit).
  const [toast, setToast] = useState<string | null>(null);
  // "Ödev Yükle" submission modal state.
  const [submitFile, setSubmitFile] = useState<File | null>(null);
  const [submitNote, setSubmitNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submitFileRef = useRef<HTMLInputElement>(null);
  const [answersRevealed, setAnswersRevealed] = useState(false);

  // "Süreyi Başlat" — open the inline test runner directly on the ödev page
  // (not a redirect to the library). The runner uses the same
  // submit-section-attempt endpoint, so the book's progress updates just
  // like it would in the library flow.
  const startAssignmentTest = (a: Assignment) => {
    // Deadline no longer locks — a past-due open homework is still solvable
    // (tagged Geç Teslim). It's locked only once the teacher closes/finalizes,
    // and closed homeworks aren't reachable here (they leave the student view).
    setModal(null);
    if (a.bookId && a.sectionLabel) setRunningTest(a);
  };

  const refreshAssignments = () => {
    studentApi.assignments()
      .then((res) => setAssignments((res.data || []).map(mapSubmission)))
      .catch(console.error);
  };

  useEffect(() => {
    studentApi.assignments()
      .then((res) => setAssignments((res.data || []).map(mapSubmission)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // After the list loads, if the navigator asked us to auto-open a specific
  // assignment, try to find + open it. This is the "exact homework popup"
  // path the spec wants for the schedule deep-link — falls back gracefully
  // when the id isn't found (e.g. archived).
  useEffect(() => {
    if (!state?.openAssignmentId || assignments.length === 0) return;
    // The plan stores the ASSIGNMENT id. Matching it against the submission id
    // opened nothing — or a different homework whose submission had that number.
    const a = assignments.find((x) => x.assignmentId === state.openAssignmentId);
    if (!a) return;
    if (a.type === 'odev') setModal({ type: 'diger-odev', assignment: a });
    else if (a.status === 'completed') setModal({ type: 'sonuclar', assignment: a });
    else setModal({ type: 'teste-basla', assignment: a });
  }, [state?.openAssignmentId, assignments]);

  const filterBySubject = (a: Assignment) =>
    !subjectFilter || (a.subject ?? '').toLowerCase() === subjectFilter.toLowerCase();
  const pendingCount = assignments.filter((a) => a.status === 'pending' && filterBySubject(a)).length;

  // T2: her ders için toplam + bekleyen ödev sayısı; çip şeridi bunları
  // gösterir. Bekleyeni çok olan ders başa gelsin, sonra Türkçe alfabetik.
  const totalPending = assignments.filter((a) => a.status === 'pending').length;
  const subjectStats = useMemo(() => {
    const map = new Map<string, { total: number; pending: number }>();
    for (const a of assignments) {
      const s = a.subject;
      if (!s) continue;
      const cur = map.get(s) ?? { total: 0, pending: 0 };
      cur.total += 1;
      if (a.status === 'pending') cur.pending += 1;
      map.set(s, cur);
    }
    return Array.from(map.entries())
      .map(([subject, stats]) => ({ subject, ...stats }))
      .sort((a, b) => b.pending - a.pending || a.subject.localeCompare(b.subject, 'tr'));
  }, [assignments]);

  // "not done" (pending) / "submitted" (turned in, not graded) / "complete" (graded).
  const statusOf = (a: Assignment): 'notdone' | 'submitted' | 'complete' =>
    a.status === 'pending' ? 'notdone' : a.graded ? 'complete' : 'submitted';

  // B5-followup (Option A): the Teslim Tarihi filter buckets by the DEADLINE
  // (raw due_date), not the submission date — "Bugün / Bu Hafta / Bu Ay" means
  // "due in this window", so pending homeworks appear too. Buckets are bounded
  // [start, end]. Süresiz (due_date null) homeworks are their own "Süresiz"
  // bucket and are EXCLUDED from the dated buckets (they'd otherwise land in
  // today+week+month at once via the created_at fallback).
  const withinWindow = (iso: string | null | undefined): boolean => {
    if (timeFilter === 'all') return true;
    if (timeFilter === 'undated') return !iso; // yalnızca süresiz (due_date yok)
    if (!iso) return false; // süresiz → dated bucket'larda gösterme
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return false;
    const now = new Date();
    let start: Date;
    let end: Date;
    if (timeFilter === 'today') {
      start = new Date(now); start.setHours(0, 0, 0, 0);
      end = new Date(now); end.setHours(23, 59, 59, 999);
    } else if (timeFilter === 'week') {
      start = new Date(now); const dow = (start.getDay() + 6) % 7; start.setDate(start.getDate() - dow); start.setHours(0, 0, 0, 0);
      end = new Date(start); end.setDate(start.getDate() + 6); end.setHours(23, 59, 59, 999);
    } else {
      start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    }
    return t >= start.getTime() && t <= end.getTime();
  };

  const handleCardClick = (a: Assignment) => {
    // Diğer Ödev always opens its own detail modal (even once submitted) —
    // the test-results modal doesn't apply to file/instruction homework.
    if (a.type === 'odev') {
      setAnswersRevealed(false);
      setModal({ type: 'diger-odev', assignment: a });
    } else if (a.status === 'completed') {
      setModal({ type: 'sonuclar', assignment: a });
    } else {
      setModal({ type: 'teste-basla', assignment: a });
    }
  };

  // Open the "Ödev Yükle" submission modal with a clean form.
  const openUploadModal = (a: Assignment) => {
    setSubmitFile(null);
    // B5: re-upload prefills the previous note so the student doesn't retype it
    // (empty on a first upload since studentNotes is unset then).
    setSubmitNote(a.studentNotes ?? '');
    setSubmitError(null);
    setModal({ type: 'odev-yukle', assignment: a });
  };

  const submitOdev = async (a: Assignment) => {
    if (!submitFile) { setSubmitError('Lütfen yüklenecek bir dosya seçin.'); return; }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { data: up } = await studentApi.uploadSubmissionFile(a.id, submitFile);
      await studentApi.completeOtherAssignment(a.id, { fileKey: up.key, fileName: up.fileName, note: submitNote.trim() || undefined });
      refreshAssignments();
      setModal(null);
      // Submitted work now lives in Notlandırma; make that discoverable and
      // remind the student they can still replace the file until it's graded.
      setTab('notlandirma');
      setToast('Ödevin gönderildi. Notlandırma sekmesinden görebilir, teslim kapanana kadar dosyanı değiştirebilirsin.');
      window.setTimeout(() => setToast(null), 6000);
    } catch (e: any) {
      setSubmitError(e?.response?.data?.error ?? e?.message ?? 'Ödev yüklenemedi.');
    } finally {
      setSubmitting(false);
    }
  };

  const displayed = assignments
    .filter(filterBySubject)
    // Ödevler = not-done only; Notlandırma = submitted (awaiting) + graded.
    .filter((a) => (tab === 'odevler' ? statusOf(a) === 'notdone' : statusOf(a) !== 'notdone'))
    // Teslim Tarihi filter only applies on the Ödevler tab.
    .filter((a) => (tab === 'notlandirma' ? true : withinWindow(a.dueDateRaw)))
    .sort((a, b) => {
      const ta = a.completedAt ? new Date(a.completedAt).getTime() : (a.closeDateRaw ? new Date(a.closeDateRaw).getTime() : 0);
      const tb = b.completedAt ? new Date(b.completedAt).getTime() : (b.closeDateRaw ? new Date(b.closeDateRaw).getTime() : 0);
      return tb - ta; // newest first
    });

  // When the runner is active we render it alone (fullscreen) so the
  // ödev list doesn't compete for attention.
  if (runningTest && runningTest.bookId && runningTest.sectionLabel) {
    return (
      <TestRunner
        bookId={runningTest.bookId}
        sectionLabel={runningTest.sectionLabel}
        testId={runningTest.testId ?? null}
        storageScope={`ass-${runningTest.id}`}
        submissionId={runningTest.id}
        onClose={() => { setRunningTest(null); refreshAssignments(); }}
        onCompleted={() => { refreshAssignments(); }}
      />
    );
  }

  return (
    <div>
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] max-w-[92vw]">
          <div className="flex items-start gap-2 bg-[#179e3b] text-white rounded-[12px] px-4 py-3 shadow-lg" style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 500 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 flex-shrink-0"><polyline points="20 6 9 17 4 12" /></svg>
            <span>{toast}</span>
          </div>
        </div>
      )}
      <div className="flex items-center gap-x-4 gap-y-2 mb-5 flex-wrap">
        <h1 style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Ödevlerim</h1>
        {/* Ödevler (yapılacak) / Notlandırma (gönderilen + notlandırılan) */}
        <SegmentedSwitch
          ariaLabel="Ödev görünümü"
          value={tab}
          onChange={setTab}
          options={[{ value: 'odevler', label: 'Ödevler' }, { value: 'notlandirma', label: 'Notlandırma' }]}
        />
        <span className="text-[14px] lg:text-[16px] text-gray-500">
          <span className="font-semibold text-[#3973bf]">{pendingCount} adet tamamlanmamış</span> ödev bulunmaktadır
        </span>
      </div>

      {/* Ders filtresi: yalnızca ödevi olan dersler; sayı etiketin içinde. */}
      <div className="space-y-3 mb-5">
        {subjectStats.length > 0 && (
          <FilterPills
            label="Ders"
            tone="blue"
            value={subjectStats.find((x) => x.subject.toLowerCase() === subjectFilter.toLowerCase())?.subject ?? ''}
            onChange={(v) => { setSubjectFilter(v === subjectFilter ? '' : v); setFromSchedule(false); }}
            options={[
              { value: '', label: `Tümü (${totalPending})` },
              ...subjectStats.map(({ subject, pending }) => ({ value: subject, label: `${subject} (${pending})` })),
            ]}
          />
        )}

        {/* Teslim tarihi: yalnızca Ödevler sekmesinde; Notlandırma sonuçlarla ilgili. */}
        {tab === 'odevler' && (
          <FilterPills
            label="Teslim tarihi"
            tone="pink"
            value={timeFilter}
            onChange={setTimeFilter}
            options={[{ value: 'all', label: 'Tüm Zamanlar' }, { value: 'today', label: 'Bugün' }, { value: 'week', label: 'Bu Hafta' }, { value: 'month', label: 'Bu Ay' }, { value: 'undated', label: 'Süresiz' }]}
          />
        )}
      </div>

      {subjectFilter && fromSchedule && (
        <div className="mb-4 flex items-center justify-between bg-[#eaf1ff] rounded-[12px] px-4 py-2" style={{ border: '1.5px solid #3973bf' }}>
          <span style={{ fontFamily: 'Inter', fontSize: 13, color: '#2b2b2b' }}>
            Çalışma Planından geldiniz: yalnızca <strong>{subjectFilter}</strong> ödevleri gösteriliyor.
          </span>
          <button
            onClick={() => { setSubjectFilter(''); setFromSchedule(false); }}
            className="text-[12px] font-semibold text-[#3973bf] hover:underline"
          >
            Filtreyi Temizle
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : displayed.length === 0 ? (
        tab === 'odevler' && studentClassStatus(user) === 'awaiting' ? (
          // Kurumlu but in no class: class homework cannot exist yet — say so, rather
          // than "no homework", which reads as the teacher having assigned nothing.
          <AwaitingClassNotice text={AWAITING_ASSIGNMENTS_TEXT} />
        ) : (
          <p className="text-center text-gray-400 py-16">
            {tab === 'odevler' ? 'Yapılacak ödevin bulunmuyor.' : 'Notlandırma bekleyen veya notlandırılmış ödevin bulunmuyor.'}
          </p>
        )
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 items-stretch">
          {displayed.map((a) => {
            const completed = a.status === 'completed';
            const draft = completed ? { started: false, answeredCount: 0 } : getAssignmentDraftProgress(a.id);
            const dl = getDeadline(a.closeDateRaw, a.closeDate, completed);
            const typeLabel = a.type === 'test' ? 'Test Ödevi' : 'Diğer Ödev';
            const iconBg = draft.started ? '#f5c518' : '#3973bf';
            const urgent = dl.tier === 'overdue' || dl.tier === 'urgent';
            const circleBg = completed ? '#179e3b' : urgent ? '#dc2626' : dl.tier === 'soon' ? '#f59e0b' : '#ffda09';
            // Restrained "red card": a colored inset left stripe instead of a
            // full red fill, so it reads at a glance without shifting layout.
            const accentShadow = dl.accent !== 'transparent' ? `, inset 4px 0 0 ${dl.accent}` : '';
            // Status line — parallel across all cards: grade state when done,
            // start state when pending.
            let statusText: string;
            let statusColor: string;
            if (completed) {
              if (a.type === 'test' || a.graded) {
                // Puan = round(100 / soru sayısı × doğru) → 0-100 ölçekli
                // gerçek bir puan, o yüzden X/100 gösterimi anlamlı.
                statusText = `Notlandırıldı · ${a.type === 'test' ? a.earnedPoints : (a.gradeScore ?? '-')}/100`;
                statusColor = '#179e3b';
              } else {
                statusText = 'Notlandırma Bekliyor';
                statusColor = '#a97e00';
              }
            } else if (draft.started) {
              statusText = 'Yarıda kaldı, devam et';
              statusColor = '#a97e00';
            } else {
              statusText = 'Henüz başlanmadı';
              statusColor = '#9ca3af';
            }
            return (
              <div
                key={a.id}
                onClick={() => handleCardClick(a)}
                className="bg-[#fafafa] rounded-[12px] p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-shadow min-h-[132px] h-full"
                style={{ boxShadow: `0px 1px 4px rgba(0,0,0,0.12)${accentShadow}` }}
              >
                <div
                  className="w-[75px] h-[75px] rounded-[8px] flex items-center justify-center flex-shrink-0"
                  style={{ background: iconBg, boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
                >
                  <LessonIcon
                    subject={a.subject}
                    level={studentLevel}
                    size={56}
                    fallback={<img src="/a-odevicon.png" alt="" className="w-[56px] h-[45px] object-contain" style={{ filter: 'drop-shadow(0px 4px 4px rgba(0,0,0,0.25))' }} />}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 700, color: '#2b2b2b' }} className="truncate pr-1">
                      {a.title}
                    </p>
                    <div className="w-[23px] h-[23px] rounded-full flex items-center justify-center flex-shrink-0" style={{ background: circleBg }}>
                      {completed
                        ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                        : urgent
                        ? <AlertIcon size={13} color="#fff" />
                        : <ClockIcon size={14} color={dl.tier === 'soon' ? '#fff' : '#1e1e1e'} />}
                    </div>
                  </div>
                  <p style={{ fontFamily: 'Inter', fontSize: 14, color: '#777', marginBottom: 4 }} className="truncate">
                    {a.subject ? `${typeLabel} · ${a.subject}` : typeLabel}
                  </p>
                  <p style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 600, color: statusColor, marginBottom: 8 }}>
                    {statusText}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <DeadlineBadge info={dl} />
                    {a.late && (
                      <span className="inline-flex items-center rounded-full px-2.5 py-1 self-start" style={{ background: '#fee2e2', color: '#dc2626', fontFamily: 'Inter', fontSize: 12.5, fontWeight: 700 }}>
                        Geç Teslim
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Teste Başla Modal */}
      {modal?.type === 'teste-basla' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setModal(null)} />
          <div className="relative bg-[#f7f7f7] rounded-[24px] w-full max-w-[1000px] p-8 lg:p-10" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
            <CloseButton onClick={() => setModal(null)} />
            <h2 style={{ fontFamily: 'Inter', fontSize: 32, fontWeight: 700, color: '#3973bf', marginBottom: 24 }}>Ödeve Başla</h2>
            <div className="bg-[#fafafa] rounded-[20px] p-5 mb-6" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
              <p style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Test</p>
              <p style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 500, color: '#2b2b2b', marginTop: 8 }}>{modal.assignment.title} - Test</p>
              {modal.assignment.teacherName && (
                <p style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 500, color: '#777', marginTop: 6 }}>
                  Öğretmen: <strong style={{ color: '#2b2b2b' }}>{modal.assignment.teacherName}</strong>
                </p>
              )}
            </div>
            <div className="flex gap-6 mb-8">
              <div className="flex-1 bg-[#fafafa] rounded-[20px] p-6 text-center" style={{ border: '4px solid #3973bf', boxShadow: '0px 1px 4px rgba(0,0,0,0.25)' }}>
                <p style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 700, color: '#3973bf' }}>Toplam Puan</p>
                <p style={{ fontFamily: 'Inter', fontSize: 40, fontWeight: 700, color: '#3973bf', marginTop: 8 }}>{modal.assignment.totalPoints}</p>
              </div>
              <div className="flex-1 bg-[#fafafa] rounded-[20px] p-6 text-center" style={{ border: '4px solid #e5599b', boxShadow: '0px 1px 4px rgba(0,0,0,0.25)' }}>
                <p style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 700, color: '#e5599b' }}>Soru Sayısı</p>
                <p style={{ fontFamily: 'Inter', fontSize: 40, fontWeight: 700, color: '#e5599b', marginTop: 8 }}>{modal.assignment.questionCount}</p>
              </div>
            </div>
            <OutcomeChips outcomes={modal.assignment.learningOutcomes} />
            <p style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#2b2b2b', marginBottom: 4 }}>Test başladığında sorularda geçirdiğin süre kaydedilecektir. Hızlı olmaya çalış!</p>
            <p style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#2b2b2b', marginBottom: 24 }}>Testi Bitirdikten sonra tekrar testi çözemeyeceksin, hazır olduğunda testi başlat.</p>
            {isPastDue(modal.assignment.closeDateRaw) && (
              <p style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 600, color: '#dc2626', marginBottom: 12 }}>
                Teslim tarihi geçti, yine de çözebilirsin, ancak ödevin “Geç Teslim” olarak işaretlenecek.
              </p>
            )}
            {/* An assignment whose test has no questions cannot be solved. This
                happens when the homework points at a book that is no longer
                reachable — measured: ALL 16 book-linked assignments reference
                retired books, carrying 39 pending submissions between them. The
                old modal still showed an enabled "Süreyi Başlat" next to
                "Soru Sayısı 0", so the student started an empty test and had no
                way to understand why. Say so plainly and disable the control
                instead. */}
            {!modal.assignment.questionCount && (
              <p style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 600, color: '#dc2626', marginBottom: 12 }}>
                Bu ödevin içeriği şu anda açılamıyor, soruları yüklenemedi. Lütfen öğretmenine bildir.
              </p>
            )}
            <div className="flex justify-end">
              <button
                onClick={() => startAssignmentTest(modal.assignment)}
                disabled={!modal.assignment.bookId || !modal.assignment.sectionLabel || !modal.assignment.questionCount}
                className="rounded-[12px] px-10 py-4 text-[20px] font-bold transition-opacity text-[#fafafa] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: '#12D700' }}
              >
                Süreyi Başlat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sonuçlar Modal */}
      {modal?.type === 'sonuclar' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setModal(null)} />
          <div className="relative bg-[#f7f7f7] rounded-[24px] w-full max-w-[1000px] p-8 lg:p-10" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
            <CloseButton onClick={() => setModal(null)} />
            <h2 style={{ fontFamily: 'Inter', fontSize: 32, fontWeight: 700, color: '#3973bf', marginBottom: 24 }}>Sonuçlar</h2>
            <div className="flex gap-6">
              <div className="flex-1 space-y-4">
                <div className="bg-[#fafafa] rounded-[20px] p-5" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                  <p style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 600, color: '#2b2b2b' }}>Ödev</p>
                  <p style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 500, color: '#2b2b2b', marginTop: 8 }}>{modal.assignment.title}</p>
                  {modal.assignment.teacherName && (
                    <p style={{ fontFamily: 'Inter', fontSize: 14, color: '#777', marginTop: 4 }}>Öğretmen: <strong>{modal.assignment.teacherName}</strong></p>
                  )}
                  {modal.assignment.completedAt && (
                    <p style={{ fontFamily: 'Inter', fontSize: 14, color: '#777', marginTop: 4 }}>
                      Tamamlandı: {new Date(modal.assignment.completedAt).toLocaleDateString('tr-TR')}
                    </p>
                  )}
                  {modal.assignment.late && (
                    <span className="inline-flex items-center rounded-full px-3 py-1 mt-2" style={{ background: '#fee2e2', color: '#dc2626', fontFamily: 'Inter', fontSize: 13, fontWeight: 700 }}>
                      Geç Teslim
                    </span>
                  )}
                </div>
                <div className="bg-[#fafafa] rounded-[20px] p-5 flex items-center gap-4" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                  <ClockIcon size={23} color="#2b2b2b" />
                  <div>
                    <p style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 500, color: '#2b2b2b' }}>Geçirilen Süre</p>
                    <p style={{ fontFamily: 'Inter', fontSize: 32, fontWeight: 600, color: '#2b2b2b', marginTop: 4 }}>{modal.assignment.timeSpent}</p>
                  </div>
                </div>
              </div>
              <div className="w-[353px] space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-[#fafafa] rounded-[20px] p-4 text-center" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                    <p style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 500, color: '#2b2b2b' }}>Soru</p>
                    <p style={{ fontFamily: 'Inter', fontSize: 28, fontWeight: 700, color: '#2b2b2b' }}>{modal.assignment.questionCount || '-'}</p>
                  </div>
                  <div className="bg-[#fafafa] rounded-[20px] p-4 text-center" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                    <p style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 500, color: '#3973bf' }}>Puan</p>
                    <p style={{ fontFamily: 'Inter', fontSize: 28, fontWeight: 700, color: '#3973bf' }}>{modal.assignment.earnedPoints}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-[#fafafa] rounded-[16px] p-3 text-center" style={{ border: '2px solid #59e56e' }}>
                    <p style={{ fontFamily: 'Inter', fontSize: 12, color: '#27a745' }}>Doğru</p>
                    <p style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 700, color: '#27a745' }}>{modal.assignment.correctCount}</p>
                  </div>
                  <div className="bg-[#fafafa] rounded-[16px] p-3 text-center" style={{ border: '2px solid #e53e3e' }}>
                    <p style={{ fontFamily: 'Inter', fontSize: 12, color: '#e53e3e' }}>Yanlış</p>
                    <p style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 700, color: '#e53e3e' }}>{modal.assignment.wrongCount}</p>
                  </div>
                  <div className="bg-[#fafafa] rounded-[16px] p-3 text-center" style={{ border: '2px solid #f5c518' }}>
                    <p style={{ fontFamily: 'Inter', fontSize: 12, color: '#a97e00' }}>Boş</p>
                    <p style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 700, color: '#a97e00' }}>{modal.assignment.blankCount}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6">
              <OutcomeChips outcomes={modal.assignment.learningOutcomes} />
            </div>
            <div className="flex gap-4 justify-end mt-8">
              <button onClick={() => setModal(null)} className="bg-white rounded-[12px] px-8 py-4 text-[20px] font-bold text-[#2b2b2b] border-2 border-gray-200 hover:bg-gray-50 transition-colors">
                Geri Dön
              </button>
              {modal?.type === 'sonuclar' && modal.assignment.bookId && (
                <button
                  onClick={() => setModal({ type: 'cevaplar', assignment: modal.assignment })}
                  className="rounded-[12px] px-8 py-4 text-[20px] font-bold text-[#fafafa] hover:opacity-90 transition-opacity"
                  style={{ background: '#e5599b' }}
                >
                  Kitabı Aç
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Diğer Ödev Modal */}
      {modal?.type === 'diger-odev' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setModal(null)} />
          <div className="relative bg-[#f7f7f7] rounded-[24px] w-full max-w-[1000px] max-h-[90vh] overflow-y-auto p-8 lg:p-10" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
            <CloseButton onClick={() => setModal(null)} />
            <h2 style={{ fontFamily: 'Inter', fontSize: 32, fontWeight: 700, marginBottom: 24 }}>
              <span style={{ color: '#2b2b2b' }}>Ödev: </span>
              <span style={{ color: '#3973bf' }}>{modal.assignment.title}</span>
            </h2>
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-1">
                <div className="mb-6 max-h-[300px] overflow-y-auto pr-2">
                  <p style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 400, color: '#2b2b2b', lineHeight: '1.6' }}>
                    {modal.assignment.teacherNote || 'Öğretmen açıklaması bulunmamaktadır.'}
                  </p>
                </div>
                {/* HW-1: optional metadata — render only the fields the
                    teacher actually filled in. */}
                {modal.assignment.otherFields && Object.keys(modal.assignment.otherFields).length > 0 && (
                  <div className="bg-white rounded-[12px] p-4 border border-gray-200">
                    <p className="text-[13px] font-bold text-[#3973bf] mb-2">Diğer Bilgiler</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[13px]">
                      {modal.assignment.otherFields.questionCount && (
                        <p><span className="text-gray-500">Soru Sayısı: </span><strong>{modal.assignment.otherFields.questionCount}</strong></p>
                      )}
                      {modal.assignment.otherFields.subject && (
                        <p><span className="text-gray-500">Ders: </span><strong>{modal.assignment.otherFields.subject}</strong></p>
                      )}
                      {modal.assignment.otherFields.unit && (
                        <p><span className="text-gray-500">Ünite: </span><strong>{modal.assignment.otherFields.unit}</strong></p>
                      )}
                      {modal.assignment.otherFields.outcome && (
                        <p className="col-span-2"><span className="text-gray-500">Kazanım: </span><strong>{modal.assignment.otherFields.outcome}</strong></p>
                      )}
                    </div>
                  </div>
                )}

                <div className="mt-4">
                  <OutcomeChips outcomes={modal.assignment.learningOutcomes} />
                </div>

                {/* Öğrenci teslimi — upload flow / submitted state */}
                <div className="mt-4 bg-white rounded-[12px] p-4 border border-gray-200">
                  <p className="text-[13px] font-bold text-[#3973bf] mb-2">Ödev Teslimi</p>
                  {modal.assignment.submissionFileUrl ? (
                    <>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#179e3b] bg-[#59e56e]/15 rounded-full px-3 py-1">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#179e3b" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                          Gönderildi
                        </span>
                        {modal.assignment.late && (
                          <span className="inline-flex items-center text-[13px] font-semibold text-[#dc2626] bg-[#fee2e2] rounded-full px-3 py-1">Geç Teslim</span>
                        )}
                      </div>
                      {modal.assignment.studentNotes && (
                        <p className="text-[13px] text-[#2b2b2b] whitespace-pre-wrap mt-2 mb-2"><span className="text-gray-500">Notun: </span>{modal.assignment.studentNotes}</p>
                      )}
                      <a href={modal.assignment.submissionFileUrl} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#3973bf] hover:underline mt-1">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3973bf" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                        Yüklediğin dosyayı görüntüle
                      </a>
                      {/* Re-upload is open until the teacher pauses/finalizes (deadline
                          no longer locks). Also locked once graded so a re-upload
                          can't revert the teacher's grade. */}
                      {!modal.assignment.answersReleased && !modal.assignment.graded && (
                        <div className="mt-3">
                          <button
                            onClick={() => setModal({ type: 'yeniden-yukle-onay', assignment: modal.assignment })}
                            className="w-full flex items-center justify-center gap-2 bg-white text-[#e5599b] border border-[#e5599b] text-[14px] font-semibold py-2.5 rounded-[12px] hover:bg-[#fdecef] transition-colors"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                            Yeniden Yükle / Dosyayı Değiştir
                          </button>
                          <p className="text-[12px] text-gray-500 mt-1.5">Yeni dosya öncekini değiştirir. Öğretmen ödevi kapatana kadar değiştirebilirsin.</p>
                        </div>
                      )}
                    </>
                  ) : modal.assignment.answersReleased ? (
                    <p className="text-[13px] text-gray-500">Bu ödev tamamlandı ve teslime kapandı. Teslim etmediğin için notun aşağıda görünmektedir.</p>
                  ) : (
                    <>
                      <p className="text-[13px] text-gray-500 mb-3">
                        Ödevini PDF veya görsel olarak yükle.
                        {isPastDue(modal.assignment.closeDateRaw) && (
                          <span className="text-[#dc2626] font-semibold"> Teslim tarihi geçti, göndereceğin ödev “Geç Teslim” olarak işaretlenecek.</span>
                        )}
                      </p>
                      <button
                        onClick={() => openUploadModal(modal.assignment)}
                        className="w-full flex items-center justify-center gap-2 bg-[#e5599b] text-white text-[14px] font-semibold py-2.5 rounded-[12px] hover:bg-pink-600 transition-colors"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                        Ödev Yükle
                      </button>
                    </>
                  )}
                </div>

                {/* Notlandırma — shown once the teacher has graded the submission */}
                {modal.assignment.graded && (
                  <div className="mt-4 bg-white rounded-[12px] p-4 border border-gray-200">
                    <p className="text-[13px] font-bold text-[#3973bf] mb-2">Notlandırma</p>
                    <div className="flex items-baseline gap-1.5 mb-2">
                      <span className="text-[13px] text-gray-500">Puan:</span>
                      <span className="text-[22px] font-bold text-[#e5599b]">{modal.assignment.gradeScore ?? '-'}</span>
                      <span className="text-[13px] text-gray-400">/ 100</span>
                    </div>
                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-[12px] text-gray-500 mb-1">Öğretmen Yorumu</p>
                      {modal.assignment.teacherFeedback ? (
                        <p className="text-[13px] text-[#2b2b2b] whitespace-pre-wrap">{modal.assignment.teacherFeedback}</p>
                      ) : (
                        <p className="text-[13px] text-gray-400">Öğretmen yorum eklemedi.</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Cevap Anahtarı — only after the teacher releases it */}
                {modal.assignment.answersReleased && modal.assignment.answerKey && (
                  <div className="mt-4 bg-white rounded-[12px] p-4 border border-gray-200">
                    <p className="text-[13px] font-bold text-[#3973bf] mb-2">Cevap Anahtarı</p>
                    {answersRevealed ? (
                      <p className="text-[14px] text-[#2b2b2b] whitespace-pre-wrap">{modal.assignment.answerKey}</p>
                    ) : (
                      <button
                        onClick={() => setAnswersRevealed(true)}
                        className="w-full flex items-center justify-center gap-2 bg-[#3973bf] text-white text-[14px] font-semibold py-2.5 rounded-[12px] hover:bg-blue-700 transition-colors"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></svg>
                        Cevapları Görüntüle
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="w-full lg:w-[460px] bg-[#fafafa] rounded-[20px] p-6 lg:flex-shrink-0" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                <p className="text-center mb-4" style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#2b2b2b' }}>
                  {modal.assignment.attachmentUrl ? 'Öğretmenin Yüklediği Dosya' : 'Dosya Bulunamadı'}
                </p>
                {modal.assignment.attachmentUrl ? (() => {
                  // The attachment URL is a signed S3 link; the file extension
                  // lives in the path before the query string. Render PDFs and
                  // images inline (read-only) and fall back to a download link
                  // for anything else (e.g. Word docs the browser won't embed).
                  const url = modal.assignment.attachmentUrl;
                  const ext = (url.split('?')[0].split('.').pop() || '').toLowerCase();
                  const isPdf = ext === 'pdf';
                  const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
                  return (
                    <div className="space-y-3">
                      {isPdf && (
                        <iframe
                          src={url}
                          title="Ödev Dosyası"
                          className="w-full h-[440px] rounded-[12px] border border-gray-200 bg-white"
                        />
                      )}
                      {isImage && (
                        <img src={url} alt="Ödev Dosyası" className="w-full rounded-[12px] border border-gray-200 bg-white" />
                      )}
                      <a href={url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 border-2 border-[#e5599b] bg-[#fdeef6] rounded-[12px] py-3 text-center hover:bg-[#fbe0ef] transition-colors">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#e5599b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                        <span className="text-[14px] font-semibold text-[#e5599b]">Yeni Sekmede Görüntüle</span>
                      </a>
                      <a href={url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 border-2 border-[#3973bf] bg-[#f0f4fa] rounded-[12px] py-3 text-center hover:bg-[#e0eaf5] transition-colors">
                        <svg width="22" height="22" viewBox="0 0 40 40" fill="none" stroke="#3973bf" strokeWidth="2.5" strokeLinecap="round">
                          <path d="M20 12v16M14 22l6 6 6-6" />
                          <path d="M6 28v4a2 2 0 002 2h24a2 2 0 002-2v-4" />
                        </svg>
                        <span className="text-[14px] font-semibold text-[#3973bf]">{isPdf || isImage ? 'Dosyayı İndir' : 'Dosyayı Aç / İndir'}</span>
                      </a>
                    </div>
                  );
                })() : (
                  <div className="border-2 border-dashed border-gray-300 rounded-[12px] p-8 flex flex-col items-center justify-center text-center">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" stroke="#c5c5c5" strokeWidth="2" strokeLinecap="round">
                      <circle cx="20" cy="20" r="16" /><line x1="12" y1="12" x2="28" y2="28" />
                    </svg>
                    <p className="text-[14px] text-gray-400 mt-2">Öğretmen dosya yüklemedi</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* B5: re-upload confirmation before replacing an already-submitted file */}
      {modal?.type === 'yeniden-yukle-onay' && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setModal({ type: 'diger-odev', assignment: modal.assignment })} />
          <div className="relative bg-[#f7f7f7] rounded-[20px] w-full max-w-[440px] p-6" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
            <h2 style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 700, color: '#e5599b', marginBottom: 8 }}>Dosyayı Değiştir</h2>
            <p style={{ fontFamily: 'Inter' }} className="text-[14px] text-[#2b2b2b] mb-5">
              Mevcut yüklediğin dosya yeni dosyayla değiştirilecek. Devam etmek istiyor musun?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setModal({ type: 'diger-odev', assignment: modal.assignment })}
                className="flex-1 rounded-[12px] py-3 bg-gray-100 text-[#2b2b2b] text-[15px] font-semibold hover:bg-gray-200"
              >Vazgeç</button>
              <button
                onClick={() => openUploadModal(modal.assignment)}
                className="flex-1 rounded-[12px] py-3 bg-[#e5599b] text-white text-[15px] font-bold hover:bg-pink-600 transition-colors"
              >Devam Et</button>
            </div>
          </div>
        </div>
      )}

      {/* Ödev Yükle — student submission upload */}
      {modal?.type === 'odev-yukle' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => { if (!submitting) setModal({ type: 'diger-odev', assignment: modal.assignment }); }} />
          <div className="relative bg-[#f7f7f7] rounded-[24px] w-full max-w-[560px] p-8" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
            <CloseButton onClick={() => { if (!submitting) setModal({ type: 'diger-odev', assignment: modal.assignment }); }} />
            <h2 style={{ fontFamily: 'Inter', fontSize: 26, fontWeight: 700, color: '#e5599b', marginBottom: 4 }}>Ödev Yükle</h2>
            <p className="text-[14px] text-gray-500 mb-5" style={{ fontFamily: 'Inter' }}>{modal.assignment.title}</p>

            {/* File picker */}
            <input
              ref={submitFileRef}
              type="file"
              accept=".pdf,image/*"
              className="hidden"
              onChange={(e) => { setSubmitFile(e.target.files?.[0] ?? null); setSubmitError(null); }}
            />
            <button
              onClick={() => submitFileRef.current?.click()}
              className="w-full rounded-[14px] px-4 py-5 flex items-center gap-3 bg-white hover:bg-gray-50 transition-colors"
              style={{ border: '2px dashed #3973bf' }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3973bf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
              </svg>
              <span className="text-[14px] font-medium text-[#3973bf] text-left flex-1 truncate">
                {submitFile ? submitFile.name : 'PDF veya görsel seç'}
              </span>
            </button>
            {submitFile && (
              <button onClick={() => setSubmitFile(null)} className="text-[12px] text-red-400 mt-1 hover:underline">Dosyayı kaldır</button>
            )}

            {/* Note */}
            <label className="block text-[14px] font-semibold text-[#2b2b2b] mt-4 mb-2" style={{ fontFamily: 'Inter' }}>Not (opsiyonel)</label>
            <textarea
              value={submitNote}
              onChange={(e) => setSubmitNote(e.target.value)}
              rows={4}
              placeholder="Öğretmenine iletmek istediğin not…"
              className="w-full bg-white rounded-[14px] px-4 py-3 outline-none text-[15px] resize-none"
              style={{ border: '2px solid #3973bf', fontFamily: 'Inter' }}
            />

            {submitError && <p className="text-[13px] text-red-500 mt-3">{submitError}</p>}

            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setModal({ type: 'diger-odev', assignment: modal.assignment })}
                disabled={submitting}
                className="flex-1 rounded-[12px] py-3 bg-gray-100 text-[#2b2b2b] text-[15px] font-semibold hover:bg-gray-200 disabled:opacity-50"
              >Vazgeç</button>
              <button
                onClick={() => submitOdev(modal.assignment)}
                disabled={submitting || !submitFile}
                className="flex-1 rounded-[12px] py-3 bg-[#e5599b] text-white text-[15px] font-semibold hover:bg-pink-600 disabled:opacity-50"
              >{submitting ? 'Yükleniyor…' : 'Ödevi Yükle'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Cevaplar Modal — kitaba yönlendirme */}
      {modal?.type === 'cevaplar' && (() => {
        const a = modal.assignment;
        // If the homework has a book backing it, open the book in the library
        // (which shows solution videos and answer key). The autoOpenSection
        // state key makes BookContent auto-navigate to the correct section.
        if (a.bookId) {
          navigate('/student/kitap-icerik', {
            state: {
              bookId: a.bookId,
              autoOpenSection: a.sectionLabel ?? undefined,
              autoOpenTestId: a.testId ?? undefined,
            },
          });
          setModal(null);
          return null;
        }
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setModal(null)} />
            <div className="relative bg-[#f7f7f7] rounded-[24px] w-full max-w-[560px] p-8" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
              <CloseButton onClick={() => setModal(null)} />
              <h2 style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 700, color: '#3973bf', marginBottom: 16 }}>Cevaplar</h2>
              <p className="text-gray-500 text-[15px]">Bu ödev için cevap detayları mevcut değil.</p>
              <button onClick={() => setModal(null)} className="mt-6 rounded-[12px] px-8 py-3 text-[16px] font-bold text-[#fafafa] hover:opacity-90" style={{ background: '#3973bf' }}>
                Kapat
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default StudentAssignments;
